// Monster properties panel for map editor
// TODO: Detect when a placed monster's stats are outdated (monster definition was updated after
// placement) and show a warning to adventure designers. Monster stats are snapshot-copied at
// placement time, so changes to monster definitions don't propagate to already-placed tokens.
// Later, surface this as a notification to adventure designers when we have a notification system.

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

/**
 * Monster properties panel component
 * Allows editing monster properties in the map editor
 */
class MonsterPropertiesPanel extends BaseComponent {
  static properties = {
    monster: { type: Object },
  };

  static styles = css`
    :host {
      display: block;
    }

    .monster-properties-panel {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.9);
      border: 1px solid #555;
      border-radius: 8px;
      padding: 16px;
      z-index: 1000;
      min-width: 280px;
      color: #fff;
    }

    .panel-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 12px;
    }

    .panel-header h3 {
      margin: 0;
      font-size: 16px;
      font-weight: bold;
    }

    .dismiss-button {
      background: none;
      border: none;
      color: #fff;
      font-size: 24px;
      line-height: 1;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 4px;
      transition: background-color 0.2s;
    }

    .dismiss-button:hover {
      background-color: rgba(255, 255, 255, 0.1);
    }

    .setting-row {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    }

    .setting-row:last-child {
      margin-bottom: 0;
    }

    .setting-row label {
      font-size: 14px;
      margin-right: 12px;
    }

    .setting-row input[type='number'] {
      width: 80px;
      padding: 4px 8px;
      background: #333;
      border: 1px solid #555;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;
    }

    .setting-row input[type='checkbox'] {
      width: 20px;
      height: 20px;
      cursor: pointer;
    }

    .setting-row input[type='number']:focus {
      outline: none;
      border-color: #0d6efd;
    }

    .monster-name {
      color: #999;
      font-size: 12px;
      margin-bottom: 12px;
    }
  `;

  constructor() {
    super();
    /** @type {import('../../types/jsdoc-types.js').MonsterPlacement|null} */
    this.monster = null;
  }

  _handleToggleGivesLight() {
    this.dispatchEvent(new CustomEvent('toggle-gives-light', { bubbles: true }));
  }

  /** @param {Event} e */
  _handleUpdateBrightRadius(e) {
    const value = Number(/** @type {HTMLInputElement} */ (e.target).value);
    this.dispatchEvent(
      new CustomEvent('update-bright-radius', {
        detail: value,
        bubbles: true,
      }),
    );
  }

  /** @param {Event} e */
  _handleUpdateDimRadius(e) {
    const value = Number(/** @type {HTMLInputElement} */ (e.target).value);
    this.dispatchEvent(
      new CustomEvent('update-dim-radius', {
        detail: value,
        bubbles: true,
      }),
    );
  }

  /** @param {Event} e */
  _handleUpdateMinPartySize(e) {
    const value = Number(/** @type {HTMLSelectElement} */ (e.target).value);
    this.dispatchEvent(
      new CustomEvent('update-min-party-size', {
        detail: value,
        bubbles: true,
      }),
    );
  }

  _handleDismiss() {
    this.dispatchEvent(new CustomEvent('dismiss', { bubbles: true }));
  }

  render() {
    if (!this.monster) {
      return html``;
    }

    return html`
      <div class="monster-properties-panel">
        <div class="panel-header">
          <h3>Monster Properties</h3>
          <button class="dismiss-button" title="Close" @click="${this._handleDismiss}">×</button>
        </div>
        <div class="monster-name">${this.monster.name || 'Unnamed Monster'}</div>
        <div class="setting-row">
          <label for="min-party-size">Min Party Size:</label>
          <select
            id="min-party-size"
            .value="${String(this.monster.minPartySize || 1)}"
            @change="${this._handleUpdateMinPartySize}"
            style="padding: 4px 8px; background: #333; border: 1px solid #555; border-radius: 4px; color: #fff; font-size: 14px;"
          >
            <option value="1">1+</option>
            <option value="2">2+</option>
            <option value="3">3+</option>
            <option value="4">4+</option>
          </select>
        </div>
        <div class="setting-row">
          <label for="gives-light">Gives Light:</label>
          <input
            id="gives-light"
            type="checkbox"
            .checked="${this.monster.givesLight || false}"
            @change="${this._handleToggleGivesLight}"
          />
        </div>
        ${
          this.monster.givesLight
            ? html`
              <div class="setting-row">
                <label for="bright-radius">Bright Radius:</label>
                <input
                  id="bright-radius"
                  type="number"
                  min="0"
                  step="1"
                  .value="${this.monster.brightRadius || 0}"
                  @input="${this._handleUpdateBrightRadius}"
                />
              </div>
              <div class="setting-row">
                <label for="dim-radius">Dim Radius:</label>
                <input
                  id="dim-radius"
                  type="number"
                  min="0"
                  step="1"
                  .value="${this.monster.dimRadius || 0}"
                  @input="${this._handleUpdateDimRadius}"
                />
              </div>
            `
            : ''
        }
      </div>
    `;
  }
}

customElements.define('monster-properties-panel', MonsterPropertiesPanel);
